import React, { useEffect } from 'react';
import {
  RiFlowChart,
  RiNodeTree,
  RiArrowRightLine,
  RiToolsLine,
  RiListCheck3,
  RiFlaskLine,
  RiExternalLinkLine,
} from 'react-icons/ri';
import { SectionLabel, ExplainerRow } from './tutorialHelpers';

const WF_NODE_CSS_ID = 'tutorial-wf-css';

export function useWfNodeCSS() {
  useEffect(() => {
    if (!document.getElementById(WF_NODE_CSS_ID)) {
      const el = document.createElement('style');
      el.id = WF_NODE_CSS_ID;
      el.textContent = `@keyframes wf-node-pop {
        from { opacity: 0; transform: scale(0.85); }
        to   { opacity: 1; transform: scale(1); }
      }`;
      document.head.appendChild(el);
    }
  }, []);
}

const WfNode = ({ color, label, sub, visible, style = {} }) =>
  visible ? (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
      border: `2px solid ${color}`, borderRadius: 6, minWidth: 90,
      background: 'var(--bg-primary, #fff)', boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      animation: 'wf-node-pop 0.25s ease-out',
      ...style,
    }}>
      <div style={{
        background: color, color: '#fff', fontSize: '0.62rem',
        fontWeight: 700, padding: '2px 8px', width: '100%',
        textAlign: 'center', letterSpacing: '0.04em', textTransform: 'uppercase',
        borderRadius: '3px 3px 0 0',
      }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: '0.65rem', color: '#555', padding: '3px 6px' }}>
          {sub}
        </div>
      )}
    </div>
  ) : null;

const WfArrow = ({ visible }) =>
  visible ? (
    <span style={{ color: 'var(--text-secondary, #aaa)', fontSize: '1rem', margin: '0 6px', alignSelf: 'center' }}>
      →
    </span>
  ) : <span style={{ margin: '0 6px', width: 20 }} />;

export function WorkflowChatDemoPanel({ step }) {
  const showDropdown     = step >= 1;
  const workflowSelected = step >= 2;
  const userTyped        = step >= 3;
  const running          = step === 4;
  const hasOutput        = step >= 5;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.82rem' }}>
      {/* Mode selector row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px',
        background: 'var(--bg-secondary, #f5f5f5)',
        borderRadius: 6, border: '1px solid var(--border-color, #ddd)',
      }}>
        <span style={{ color: 'var(--text-secondary, #888)', fontSize: '0.72rem' }}>Mode:</span>
        <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>⬡</span>
        <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>✦</span>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem',
          border: '1px solid var(--border-color, #ccc)',
          background: workflowSelected ? '#636cff' : 'transparent',
          color: workflowSelected ? '#fff' : '#333',
          transition: 'all 0.2s',
          position: 'relative',
        }}>
          <RiFlowChart size={12} />
          {workflowSelected ? 'Tool to Agent' : 'Workflows'}
          <span style={{ fontSize: '0.55rem' }}>▾</span>

          {showDropdown && !workflowSelected && (
            <div style={{
              position: 'absolute', top: '110%', left: 0, zIndex: 10,
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #ddd)',
              borderRadius: 6, minWidth: 170, padding: '4px 0',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              animation: 'wf-node-pop 0.18s ease-out',
            }}>
              <div style={{ padding: '3px 10px', fontSize: '0.65rem', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Platform</div>
              {[
                { icon: '⚙', name: 'Tool to Agent', active: true },
                { icon: '🤖', name: 'Agent Only' },
                { icon: '🔀', name: 'Route by Score' },
                { icon: '🔬', name: 'Research Brief' },
                { icon: '⚖️', name: 'Parallel Perspectives' },
              ].map(({ icon, name, active }) => (
                <div key={name} style={{
                  padding: '5px 10px', display: 'flex', alignItems: 'center', gap: 6,
                  background: active ? 'var(--hover-color, #f0f0f0)' : 'transparent',
                  cursor: 'pointer', fontSize: '0.78rem',
                  color: active ? '#636cff' : '#333',
                  fontWeight: active ? 600 : 400,
                }}>
                  <span style={{ fontSize: '0.85rem' }}>{icon}</span> {name}
                </div>
              ))}
            </div>
          )}
        </div>
        <span style={{ fontSize: '0.72rem', color: '#636cff', marginLeft: 'auto' }}>
          {workflowSelected ? '⚙ Workflow mode active' : ''}
        </span>
      </div>

      {/* Canvas simulation */}
      {workflowSelected && (
        <div style={{
          padding: '8px 10px',
          background: 'var(--bg-secondary, #f8f8f8)',
          borderRadius: 6, border: '1px solid var(--border-color, #eee)',
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: '0.72rem', color: 'var(--text-secondary, #888)',
        }}>
          <WfNode color="#22c55e" label="Input" sub="var: query" visible />
          <WfArrow visible />
          <WfNode color="#3b82f6" label="Agent" sub="generalist" visible />
          <WfArrow visible />
          <WfNode color="#ef4444" label="Output" sub="var: result" visible />
        </div>
      )}

      {/* Chat input */}
      {workflowSelected && (
        <div style={{
          padding: '6px 10px',
          border: '1px solid var(--border-color, #ddd)',
          borderRadius: 6, background: 'var(--bg-primary, #fff)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            flex: 1, fontSize: '0.8rem',
            color: userTyped ? '#333' : '#888',
            fontStyle: userTyped ? 'normal' : 'italic',
          }}>
            {userTyped ? 'What are recent AI breakthroughs?' : 'Type your input here…'}
          </span>
          <button style={{
            padding: '3px 12px', borderRadius: 4, border: 'none',
            background: running ? 'rgba(99,108,255,0.6)' : '#636cff',
            color: '#fff', fontSize: '0.78rem', fontWeight: 600,
            cursor: 'pointer', opacity: !userTyped ? 0.4 : 1,
          }}>
            {running ? '⏳ Running…' : 'Run'}
          </button>
        </div>
      )}

      {/* Output card */}
      {hasOutput && (
        <div style={{
          border: '1px solid var(--border-color, #ddd)',
          borderRadius: 8, overflow: 'hidden',
          animation: 'wf-node-pop 0.3s ease-out',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '5px 10px',
            background: 'var(--bg-secondary, #f5f5f5)',
            borderBottom: '1px solid var(--border-color, #eee)',
            fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary, #888)',
          }}>
            <span>⚙ Tool to Agent</span>
            <span style={{ opacity: 0.6 }}>Copy</span>
          </div>
          <div style={{ padding: '8px 10px', fontSize: '0.8rem', color: '#333', lineHeight: 1.5 }}>
            <p style={{ margin: 0 }}>
              <strong style={{ color: 'inherit' }}>Recent AI Breakthroughs:</strong>{' '}
              Large multimodal models now handle text, images, and code in a single pass.
              Retrieval-augmented generation (RAG) dramatically improves factual accuracy,
              and agentic pipelines enable autonomous multi-step task completion…
            </p>
          </div>
        </div>
      )}

      {!workflowSelected && !showDropdown && (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#888', fontSize: '0.85rem', fontStyle: 'italic',
          padding: '1rem',
        }}>
          Press <strong style={{ margin: '0 4px', color: 'inherit' }}>Tutorial</strong> to see workflow mode in action
        </div>
      )}
    </div>
  );
}

export function WorkflowExplainer() {
  return (
    <div>
      <SectionLabel>What is a Workflow?</SectionLabel>
      <ExplainerRow
        Icon={RiNodeTree} iconColor="#636cff"
        label="Visual Pipelines"
        description="Workflows chain AI agents, search tools, and logic nodes into a repeatable pipeline. You define the flow once, then run it with any input — ideal for recurring research, analysis, or generation tasks."
      />
      <ExplainerRow
        Icon={RiArrowRightLine} iconColor="#22c55e"
        label="Run from the Chat Panel"
        description="Click the Workflows dropdown (⚙) in the mode selector, choose a workflow, type your query, and press Run. The result appears inline — just like Ask mode but powered by your custom pipeline."
      />

      <SectionLabel>Platform Workflows (built-in)</SectionLabel>
      <ExplainerRow
        Icon={RiToolsLine} iconColor="#f59e0b"
        label="Tool to Agent"
        description="Runs a DuckDuckGo web search first, then passes the results to a Generalist agent for synthesis. Great for current-events questions that benefit from fresh web data."
      />
      <ExplainerRow
        Icon={RiFlowChart} iconColor="#3b82f6"
        label="Agent Only"
        description="Sends your query directly to a Generalist agent (with web search enabled). Fast and capable — no tool preprocessing overhead."
      />
      <ExplainerRow
        Icon={RiListCheck3} iconColor="#8b5cf6"
        label="Route by Score"
        description="Generalist answers first. An evaluation score then decides whether to deepen the response with the Researcher. Quality-gated and efficient."
      />
      <ExplainerRow
        Icon={RiFlaskLine} iconColor="#06b6d4"
        label="Research Brief"
        description="A three-stage pipeline: Researcher gathers raw sources → Extractor pulls structured fields → Generalist writes a polished executive brief."
      />
      <ExplainerRow
        Icon={RiNodeTree} iconColor="#10b981"
        label="Parallel Perspectives"
        description="A Generalist and a Challenger argue opposite sides of a topic in parallel, then a Generalist synthesizes a balanced conclusion. Shows off fan-out execution — both agents run at once."
      />

      <SectionLabel>Build Your Own</SectionLabel>
      <ExplainerRow
        Icon={RiExternalLinkLine} iconColor="#636cff"
        label="Workflow Builder"
        description="Open the Workflows menu (top-right) to enter the visual canvas editor. Drag Input, Agent, Tool, and Output nodes onto the canvas, connect them, choose an icon, and save — your workflow then appears in the chat panel dropdown."
      />
    </div>
  );
}
